import React from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Typography
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HistoryIcon from "@mui/icons-material/History";
import { COLORS } from "./constants";

// ─────────────────────────────────────────────────────────────────────────────
// DeleteConfirmDialog — removing an entry from the BROADCAST LOG.
//
// It used to carry a switch, defaulted ON, offering to "also delete the video
// file from server". That put two unrelated decisions behind one button:
// tidying up a log line, and destroying an uploaded video. Someone clearing old
// entries could lose a file they still wanted, and the default made that the
// likely outcome rather than the careful one.
//
// The video now has its own delete, in the library where the video is. This
// dialog only ever removes the log entry.
//
// It still has two shapes, because the same row means different things
// depending on the job:
//
//   • Still scheduled or live → removing it STOPS the broadcast. Said plainly,
//     because that is not something to discover afterwards.
//   • Already finished, cancelled or failed → nothing is running; this is
//     simply tidying the list.
// ─────────────────────────────────────────────────────────────────────────────

const ACTIVE_STATUSES = ["scheduled", "pending", "starting", "live", "running", "connecting"];

export function DeleteConfirmDialog({ job, open, onClose, onConfirm }) {
  if (!job) return null;

  const isActive = ACTIVE_STATUSES.includes(String(job.status || "").toLowerCase());
  const name = job.originalName || "this stream";

  return (
    <Dialog
      open={open} onClose={onClose}
      PaperProps={{
        sx: { bgcolor: "#0C111B", border: `1px solid ${COLORS.borderStrong}`, borderRadius: "12px", minWidth: 360 }
      }}
    >
      <DialogTitle sx={{ color: COLORS.text, fontWeight: 700, pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {isActive
            ? <WarningAmberIcon sx={{ color: COLORS.amber }} />
            : <HistoryIcon sx={{ color: COLORS.muted }} />}
          <span>{isActive ? "Stop and remove" : "Remove from log"}</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ color: COLORS.muted, fontSize: 14 }}>
          {isActive ? "Stop and remove " : "Remove "}
          <Box component="span" sx={{ color: COLORS.text, fontWeight: 700 }}>{name}</Box>
          {isActive ? " from the broadcast log?" : " from the broadcast log?"}
        </Typography>

        {isActive && (
          <Typography sx={{ color: COLORS.amber, fontSize: 12.5, mt: 1.25, lineHeight: 1.6 }}>
            This broadcast has not finished. Removing it stops it now.
          </Typography>
        )}

        {/* The reassurance that used to be a switch. Stated every time, because
            the previous behaviour trained people to expect the file to go. */}
        <Box sx={{
          mt: 2, p: 1.25, borderRadius: "10px",
          bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${COLORS.border}`
        }}>
          <Typography sx={{ color: COLORS.muted, fontSize: 12.5, lineHeight: 1.6 }}>
            The uploaded video is <Box component="span" sx={{ color: COLORS.text, fontWeight: 700 }}>not</Box> deleted
            — it stays in your library and can be scheduled again. To delete the video
            itself, use the delete button next to it in the library.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: COLORS.muted, textTransform: "none", borderRadius: "12px" }}>
          Keep
        </Button>
        <Button
          onClick={() => { onConfirm(job); onClose(); }}
          variant="contained"
          sx={{
            bgcolor: COLORS.dangerFill, color: "#fff", textTransform: "none", fontWeight: 600,
            borderRadius: "12px", boxShadow: "none", "&:hover": { bgcolor: "#BC3237", boxShadow: "none" }
          }}
        >
          {isActive ? "Stop & remove" : "Remove from log"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
